import React, { useContext, useEffect } from 'react'

import { Box, Grid, GridProps, makeAppStyles, Typography, useBreakpoint } from '@percept/mui'

import { Breakpoint } from '@material-ui/core/styles/createBreakpoints'

import { DashboardNews } from './DashboardNews'

import {
  OrgUnitPerformanceDetailCards,
  OrgUnitPerformanceSummary,
} from './OrgUnitPerformance'

import { OrgUnitCampaignSetup } from './OrgUnitCampaignSetup'

// import { OrgUnitCampaignSetupTimeseries } from './OrgUnitCampaignSetupTimeseries'

// import { OrgUnitPotentialEfficiencies } from './OrgUnitPotentialEfficiencies'

import { DateHeader } from './DateHeader'

import { stringifyUrlSearchParams, useUrlState } from '@percept/hooks'

import { UserPrivilegeContext } from '@percept/app-components'

import { OrgUnitHomePageContext, OrgUnitHomePageUrlParams } from 'contexts'

import { DashboardReferenceDatesProvider } from './ReferenceDatesProvider'

import { useSavedQueryStrings } from 'hooks'

import qs from 'query-string'

import { get } from 'lodash-es'

import { AppBranding, WorkloadContext } from '@percept/types'

import { DashboardQueryStringStaticKey } from 'redux/types'


const brandedPlatformNameMap: Partial<Record<AppBranding, string>> = {
  APERTO: 'Aperto Platform',
}

const platformNameMap: Record<WorkloadContext, string> = {
  PERCEPT: 'Percept Platform',
  VODAFONE: 'Media Quality Dashboard',
}


const useDashboardWelcomeStyles = makeAppStyles( theme => ({
  welcomeText: {
    fontSize: 24,
    margin: theme.spacing(6, 0),
    [theme.breakpoints.down('lg')]: {
      margin: theme.spacing(5, 0),
      fontSize: 22,
    },
    [theme.breakpoints.down('md')]: {
      margin: theme.spacing(5, 0),
      fontSize: 20,
    },
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(4, 0),
      fontSize: 18,
    },
  },
}))

const DashboardWelcome = (): JSX.Element => {
  const { user } = useContext(UserPrivilegeContext)

  const userName = user.name.split(' ')[0]

  const platformName = (
    brandedPlatformNameMap[process.env.BRANDING]
    || platformNameMap[process.env.WORKLOAD_CONTEXT]
    || platformNameMap.PERCEPT
  )

  const classes = useDashboardWelcomeStyles()

  return (
    <Typography className={classes.welcomeText} color='textSecondary' variant='h3' align='center'>
      Hi <Typography color='textPrimary' variant='inherit'>{userName}</Typography>, welcome to the {platformName}
    </Typography>
  )
}


type DashboardCardItem = (
  | 'PERFORMANCE_SUMMARY'
  | 'CAMPAIGN_SETUP_SCORE_SUMMARY'
  // | 'CAMPAIGN_SETUP_TIMESERIES'
  // | 'POTENTIAL_EFFICIENCIES'
  | 'PERFORMANCE_DETAIL'
  | 'NEWS'
)

const breadkpointCardOrdering: Record<Breakpoint, DashboardCardItem[]> = {
  xs: [
    'PERFORMANCE_SUMMARY',
    'CAMPAIGN_SETUP_SCORE_SUMMARY',
    // 'CAMPAIGN_SETUP_TIMESERIES',
    // 'POTENTIAL_EFFICIENCIES',
    'PERFORMANCE_DETAIL',
    'NEWS',
  ],
  sm: [
    'PERFORMANCE_SUMMARY',
    'CAMPAIGN_SETUP_SCORE_SUMMARY',
    // 'CAMPAIGN_SETUP_TIMESERIES',
    // 'POTENTIAL_EFFICIENCIES',
    'PERFORMANCE_DETAIL',
    'NEWS',
  ],
  md: [
    'PERFORMANCE_SUMMARY',
    'CAMPAIGN_SETUP_SCORE_SUMMARY',
    // 'CAMPAIGN_SETUP_TIMESERIES',
    // 'POTENTIAL_EFFICIENCIES',
    'PERFORMANCE_DETAIL',
    'NEWS',
  ],
  lg: [
    'PERFORMANCE_SUMMARY',
    'CAMPAIGN_SETUP_SCORE_SUMMARY',
    'NEWS',
    // 'CAMPAIGN_SETUP_TIMESERIES',
    // 'POTENTIAL_EFFICIENCIES',
    'PERFORMANCE_DETAIL',
  ],
  xl: [
    'PERFORMANCE_SUMMARY',
    'CAMPAIGN_SETUP_SCORE_SUMMARY',
    'NEWS',
    // 'CAMPAIGN_SETUP_TIMESERIES',
    // 'POTENTIAL_EFFICIENCIES',
    'PERFORMANCE_DETAIL',
  ],
}

const gridItemOverrides: Partial<Record<DashboardCardItem, Partial<GridProps>>> = {
  // CAMPAIGN_SETUP_TIMESERIES: {
  //   md: 12,
  //   lg: 12,
  //   xl: 12,
  // },
  PERFORMANCE_DETAIL: {
    md: 8,
    // lg: 8,
    lg: 12,
    // xl: 6,
    xl: 12,
  },
  NEWS: {
    md: 4,
  },
}


const defaultGridItemProps: Partial<GridProps> = {
  xs: 12,
  sm: 12,
  md: 6,
  lg: 4,
  // xl: 3,
}


const useStyles = makeAppStyles({
  dashboardCard: {
    flexGrow: 1,
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
})


const qsKey: DashboardQueryStringStaticKey = 'newDashboardHome'

export const DashboardHome = (): JSX.Element => {

  const [params, setSavedQueryStrings] = useSavedQueryStrings()

  const queryParams = qs.parse(params[qsKey] || '')

  const contextValue = useUrlState<OrgUnitHomePageUrlParams>({
    range: get(queryParams, 'range',  '7-days') as OrgUnitHomePageUrlParams['range'],
  })

  const state = contextValue[0]

  useEffect(() => {
    setSavedQueryStrings({ [qsKey]: stringifyUrlSearchParams(state) })
  }, [state, setSavedQueryStrings])

  const breakpoint = useBreakpoint()

  const classes = useStyles()

  const cardContents: Record<DashboardCardItem, JSX.Element> = {
    PERFORMANCE_SUMMARY: <OrgUnitPerformanceSummary className={classes.dashboardCard} />,
    CAMPAIGN_SETUP_SCORE_SUMMARY: <OrgUnitCampaignSetup className={classes.dashboardCard} />,
    // CAMPAIGN_SETUP_TIMESERIES: <OrgUnitCampaignSetupTimeseries className={classes.dashboardCard} />,
    PERFORMANCE_DETAIL: <OrgUnitPerformanceDetailCards className={classes.dashboardCard} />,
    // POTENTIAL_EFFICIENCIES: <OrgUnitPotentialEfficiencies className={classes.dashboardCard} />,
    NEWS: <DashboardNews className={classes.dashboardCard} />,
  }

  const cardOrder = breadkpointCardOrdering[breakpoint]

  return (
    <Box pl={6} pb={6} mt={2} maxWidth='100%'>
      <OrgUnitHomePageContext.Provider value={contextValue}>
        <DashboardReferenceDatesProvider>
          <DateHeader mb={3} />
          <Grid container spacing={6} xs={12}>
            { cardOrder.map( cardItem => (
              <Grid key={cardItem} item {...defaultGridItemProps} {...gridItemOverrides[cardItem]}>
                { cardContents[cardItem] }
              </Grid>
            ))}
          </Grid>
        </DashboardReferenceDatesProvider>
      </OrgUnitHomePageContext.Provider>

    </Box>
  )
}
